<template>
  <div :class="{ 'custom-select': true, open: open, 'invert-to-top': calculatePosition() }" ref="dropdownMenu" :tabindex="tabindex" @focusout="checkFocusOut($event)" @keydown="moveOption($event)" @keyup.tab="handleFocusIn()">
    <div ref="dropdownSelection" :class="{ selected: true, open: open, disabled: disabled, readonly: readonly, invalid: invalid }" @click="handleFocusIn()">
      {{ selected || '&nbsp;' }}
    </div>
    <label>{{ label }}</label>
    <label v-if="showOverride" class="override-label" @click="$emit('override')">Override</label>
    <div class="items" ref="items" :class="{ selectHide: !open }">
      <div v-if="canBeEmpty" @click="selectOption('')" :class="{ hovering: focusedIndex == -1 }" @mousemove="hoverItem($event, -1)">&nbsp;</div>
      <div v-for="(option, i) of options" :key="i" :style="{ display: option === '' ? 'none' : '' }" @click="selectOption(option)" :class="{ hovering: focusedIndex == i }" @mousemove="hoverItem($event, i)">
        {{ option.text || option }}
      </div>
      <span class="bottom-border"></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  emits: ['update:modelValue', 'change'],
  props: {
    options: {
      type: Array || Object,
      required: true,
    },
    tabindex: {
      type: String,
      default: '0',
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    canBeEmpty: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showOverride: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    override: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      open: false,
      focusedIndex: null,
      pointerLocation: { x: null, y: null },
    };
  },
  computed: {
    selected: {
      get() {
        if (this.modelValue === '') {
          return '';
        }
        if (this.options[0] && this.options[0].value !== undefined) {
          const selectedOption = this.options.find((option) => option.value === this.modelValue);
          if (selectedOption) {
            return selectedOption.text;
          } else {
            return this.modelValue;
          }
        } else {
          return this.modelValue;
        }
      },
      set(value) {
        if (value === this.modelValue) return;
        this.$emit('update:modelValue', value);
        this.$emit('change', value);
      },
    },
  },
  methods: {
    selectOption(option, dontClose) {
      if (this.disabled || this.readonly) return;
      const value = option === '' ? '' : option.value !== undefined ? option.value : option;
      this.selected = value;
      if (!dontClose) this.open = false;
    },
    moveOption(event) {
      if (this.disabled || this.readonly) return;
      if (event.key === 'ArrowDown') {
        if (this.focusedIndex === null) this.focusedIndex = this.canBeEmpty ? -1 : 0;
        else this.focusedIndex = this.focusedIndex + 1;
        const maxIndex = this.options.length - 1;
        if (this.focusedIndex > maxIndex) this.focusedIndex = maxIndex;
      } else if (event.key === 'ArrowUp') {
        this.focusedIndex = this.focusedIndex - 1;
        const minIndex = this.canBeEmpty ? -1 : 0;
        if (this.focusedIndex < minIndex) this.focusedIndex = minIndex;
      } else if (event.key === 'Enter' && this.focusedIndex !== null) {
        if (this.focusedIndex === -1 && this.canBeEmpty) {
          this.selectOption('');
        } else {
          this.selectOption(this.options[this.focusedIndex]);
        }
        return;
      } else if (event.key === 'Escape') {
        this.open = false;
        return;
      } else if (event.key !== 'Tab') {
        const letter = event.key.toLowerCase();
        const option = this.options.find((option) => {
          if (option.text) {
            return option.text.toLowerCase().startsWith(letter);
          } else {
            return option.toLowerCase().startsWith(letter);
          }
        });
        const index = this.options.indexOf(option);

        if (index !== undefined) this.focusedIndex = index;
      }

      if (this.focusedIndex >= (this.canBeEmpty ? -1 : 0) && this.focusedIndex < this.options.length && this.focusedIndex !== null) {
        if (this.focusedIndex === -1 && this.canBeEmpty) {
          this.selectOption('', true);
        } else {
          const newSelectedOption = this.options[this.focusedIndex];
          this.selectOption(newSelectedOption, true);
        }
      }

      this.$nextTick(() => {
        this.scrollToHovering();
      });
    },
    scrollToHovering() {
      const items = this.$refs.items;
      const hovering = this.$refs.items.querySelector('.hovering');
      if (hovering) {
        if (this.$refs.dropdownMenu.classList.contains('invert-to-top')) {
          items.scrollTop = hovering.offsetTop + items.offsetTop / 2;
        } else {
          items.scrollTop = hovering.offsetTop - items.offsetTop;
        }
      }
    },
    hoverItem(event, index) {
      const x = event.clientX;
      const y = event.clientY;
      if (this.pointerLocation.x === x && this.pointerLocation.y === y) return;
      this.pointerLocation = { x, y };
      this.focusedIndex = index;
    },
    checkFocusOut(event) {
      if (!this.$refs.dropdownMenu.contains(event.relatedTarget) && !this.$refs.items.contains(event.relatedTarget)) {
        this.open = false;
      }
    },
    handleFocusIn() {
      if (this.disabled || this.readonly) return;
      this.open = !this.open;
      if (this.open) {
        this.focusedIndex = null;
      }
    },
    calculatePosition() {
      const selectionEl = this.$refs.dropdownSelection;
      if (!selectionEl) return false;
      const itemsRect = selectionEl.getBoundingClientRect();
      return itemsRect.bottom > window.innerHeight - 200;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-select {
  position: relative;
  text-align: left;
  outline: none;
  max-width: 100%;
  min-width: 0;

  &.open {
    z-index: 1001;
  }

  .selected {
    background-color: white;
    border-radius: $borderRadius4;
    border: 1px solid $inputBorderColor;
    color: $textColor87;
    line-height: 1.5;
    cursor: pointer;
    user-select: none;
    padding: 0.85rem 2.5rem 0.7rem 0.7rem;
    height: 1.313rem;
    font-size: $fontSize14;
    transition: border-color 0.15s ease-in-out;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    box-sizing: border-box;
    height: 100%;

    &.disabled:not(.readonly) {
      color: $textColor12;
    }

    &.readonly {
      background: $textColor2;
    }

    &.open {
      border-radius: $borderRadius4 $borderRadius4 0px 0px;
      border: solid 2px $primaryActionColor;
      padding: calc(0.85rem - 1px) calc(2.5rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    &.invalid {
      border: 2px solid $alertColor;
      padding: calc(0.85rem - 1px) calc(2.5rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    &:after {
      position: absolute;
      content: '';
      top: 22px;
      right: 1rem;
      width: 0;
      height: 0;
      border: 5px solid transparent;
      border-color: $textColor87 transparent transparent transparent;
    }
  }

  .selected.open + label {
    color: $primaryActionColor;
  }

  .selected.invalid + label {
    color: $alertColor;
  }

  label {
    position: absolute;
    top: -0.32rem;
    left: 0.55rem;
    font-size: $fontSize10;
    color: $textColor60;
    background: white;
    padding: 0 0.3rem;
    border-radius: $borderRadius10;
    line-height: 1;
    user-select: none;
  }

  .override-label {
    position: absolute;
    top: -0.32rem;
    right: 1rem;
    left: auto;
    color: $primaryActionColor;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .items {
    color: $textColor87;
    overflow: hidden;
    position: absolute;
    background-color: white;
    left: 0;
    right: 0;
    z-index: 1001;
    border: 1px solid $inputBorderColor;
    border-top: none;
    border-bottom: none;
    max-height: 12rem;
    overflow-y: auto;

    div {
      font-size: $fontSize14;
      color: $textColor87;
      padding: 0.6rem 2.5rem 0.6rem 0.7rem;
      cursor: pointer;
      user-select: none;

      &.hovering {
        background-color: $arrowGrayColor;
      }
    }

    .bottom-border {
      height: 1px;
      background-color: $inputBorderColor;
      position: sticky;
      display: block;
      bottom: 0;
      left: 0;
      right: 0;
    }
  }

  &.invert-to-top {
    .items {
      bottom: 100%;
      top: auto;
      border-top: 1px solid $inputBorderColor;
      border-bottom: none;
    }

    .selected.open {
      border-radius: 0px 0px $borderRadius4 $borderRadius4;
    }
    label {
      z-index: 2;
    }
  }
}

.selectHide {
  display: none;
}
</style>
